import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  deleteFacilityAsset,
  getFacilityAsset,
  getFacilityAssetDowntime,
  startFacilityAssetDowntime,
  stopFacilityAssetDowntime,
  updateFacilityAsset,
  listAssetVendors,
  setAssetVendors,
  listFacilityVendors,
  listFacilityGroups,
} from "../lib/api";
import { useApi, useMutation } from "../lib/useApi";
import { formatDate, formatDateShort } from "../lib/format";
import { FACILITY_CATEGORIES, FACILITY_WO_TYPES } from "../lib/constants";
import type {
  CreateFacilityAssetInput,
  FacilityAssetDowntimeRow,
  FacilityAssetDowntimeStats,
  FacilityAssetVendorRow,
} from "../lib/types";
import { Alert } from "../components/States";
import { Modal } from "../components/Modal";
import { PageBar } from "../components/Layout";
import { confirmDialog } from "../components/ConfirmDialog";
import { EmptyState, ErrorState, Loading, Spinner } from "../components/States";
import { facilityLabel } from "./FacilitiesPage";
import { FacilityStatusBadge, FacilityPriorityBadge } from "./FacilitiesPage";
import { FacilityAssetModal, FacilityAssetStatusBadge } from "./FacilityAssetsPage";
import { RichBody, richTextIsEmpty } from "../components/RichText";

/** Human-readable span for a downtime duration: days/hours/minutes. */
function formatDowntimeSpan(totalSeconds: number): string {
  if (totalSeconds <= 0) return "0m";
  const d = Math.floor(totalSeconds / 86400);
  const h = Math.floor((totalSeconds % 86400) / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const parts: string[] = [];
  if (d) parts.push(`${d}d`);
  if (h) parts.push(`${h}h`);
  if (m || parts.length === 0) parts.push(`${m}m`);
  return parts.join(" ");
}

/** Equipment detail — editable asset fields + the work orders linked to it. */
export function FacilityAssetDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { data, loading, error, reload } = useApi(
    (signal) => getFacilityAsset(id, signal),
    [id],
  );
  const deleteMut = useMutation(deleteFacilityAsset);
  const [editing, setEditing] = useState(false);
  const groupsApi = useApi((signal) => listFacilityGroups(signal), []);
  const groups = groupsApi.data?.groups ?? [];

  async function onDelete() {
    if (!data) return;
    const yes = await confirmDialog({
      title: "Delete equipment",
      message: `Delete equipment "${data.asset.name}"? This can't be undone.`,
      danger: true,
    });
    if (!yes) return;
    const ok = await deleteMut.run(id);
    if (ok) navigate("/facilities/assets");
  }

  const asset = data?.asset;

  return (
    <>
      <PageBar
        crumbs={
          <>
            <Link to="/facilities/assets">Equipment</Link> <span aria-hidden="true">/</span> Detail
          </>
        }
        actions={
          asset ? (
            <>
              <button className="btn small" onClick={() => setEditing(true)}>
                Edit
              </button>
              <button className="btn danger small" disabled={deleteMut.pending} onClick={onDelete}>
                {deleteMut.pending ? <Spinner label="Deleting…" /> : "Delete"}
              </button>
            </>
          ) : undefined
        }
      />
      <div className="content">
        {loading ? (
          <Loading label="Loading equipment…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : !asset ? (
          <EmptyState>Equipment not found.</EmptyState>
        ) : (
          <>
            <div className="page-head">
              <div>
                <h1>{asset.name}</h1>
                <div className="sub">
                  {asset.asset_tag ? <span className="mono faint">{asset.asset_tag}</span> : null}
                  {asset.asset_tag ? " · " : ""}
                  <FacilityAssetStatusBadge status={asset.status} />
                </div>
              </div>
            </div>

            <div className="detail-grid">
              <div className="detail-main">
                <div className="card">
                  <div className="card-head">
                    <h3>Details</h3>
                  </div>
                  <div className="card-body">
                    <dl className="kv">
                      <dt>Category</dt>
                      <dd>{facilityLabel(FACILITY_CATEGORIES, asset.category)}</dd>
                      <dt>Building</dt>
                      <dd>{asset.building || "—"}</dd>
                      <dt>Location</dt>
                      <dd>{asset.location || "—"}</dd>
                      <dt>Status</dt>
                      <dd>
                        <FacilityAssetStatusBadge status={asset.status} />
                      </dd>
                      <dt>Manufacturer</dt>
                      <dd>{asset.manufacturer || "—"}</dd>
                      <dt>Model</dt>
                      <dd>{asset.model || "—"}</dd>
                      <dt>Serial</dt>
                      <dd>{asset.serial || "—"}</dd>
                      <dt>Group</dt>
                      <dd>
                        {asset.group_id
                          ? groups.find((g) => g.id === asset.group_id)?.name ?? asset.group_id
                          : "—"}
                      </dd>
                    </dl>
                    <div className="field" style={{ marginTop: 12 }}>
                      <span className="label">Notes</span>
                      {!richTextIsEmpty(asset.notes ?? "") ? (
                        <RichBody html={asset.notes ?? ""} />
                      ) : (
                        <p className="muted" style={{ margin: 0 }}>—</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="card-head">
                    <h3>Maintenance history</h3>
                    <span className="faint">{asset.requests.length}</span>
                  </div>
                  <div className="card-body">
                    {asset.requests.length === 0 ? (
                      <p className="muted">No work orders logged against this equipment.</p>
                    ) : (
                      <ul className="link-list">
                        {asset.requests.map((r) => (
                          <li key={r.id} className="facility-linked-wo">
                            <Link to={`/facilities/${r.id}`}>
                              <span className="mono faint">#{r.number}</span> {r.title}
                            </Link>
                            <div className="facility-linked-wo-meta">
                              <FacilityStatusBadge status={r.status} />{" "}
                              <FacilityPriorityBadge priority={r.priority} />{" "}
                              <span className="pill">
                                {facilityLabel(FACILITY_WO_TYPES, r.wo_type)}
                              </span>
                              <span className="faint"> · {formatDateShort(r.created_at)}</span>
                              {r.due_date ? (
                                <span className="faint"> · due {formatDateShort(r.due_date)}</span>
                              ) : null}
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>

              <div className="detail-side">
                <DowntimeSection assetId={asset.id} onAssetChanged={reload} />
                <ServiceVendorsSection assetId={asset.id} />
              </div>
            </div>
          </>
        )}
      </div>

      {editing && asset ? (
        <FacilityAssetModal
          asset={asset}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            reload();
          }}
          onUpdate={async (patch: CreateFacilityAssetInput) => {
            const res = await updateFacilityAsset(asset.id, patch);
            return !!res;
          }}
        />
      ) : null}
    </>
  );
}

/** Downtime tracking card: current up/down state, availability over the last 90
 *  days, total downtime, a Start/Stop control, and the interval log. */
function DowntimeSection({
  assetId,
  onAssetChanged,
}: {
  assetId: string;
  onAssetChanged: () => void;
}) {
  const { data, loading, error, reload } = useApi(
    (signal) => getFacilityAssetDowntime(assetId, signal),
    [assetId],
  );
  const startMut = useMutation(startFacilityAssetDowntime);
  const stopMut = useMutation(stopFacilityAssetDowntime);
  const [showStart, setShowStart] = useState(false);

  async function onStart(reason: string) {
    const res = await startMut.run(assetId, reason.trim() ? { reason: reason.trim() } : {});
    if (res) {
      setShowStart(false);
      reload();
      onAssetChanged();
    }
  }

  async function onStop() {
    const res = await stopMut.run(assetId);
    if (res) {
      reload();
      onAssetChanged();
    }
  }

  return (
    <div className="card">
      <div className="card-head">
        <h3>Downtime</h3>
        {data ? (
          <span className={data.stats.is_down ? "badge danger" : "badge ok"}>
            {data.stats.is_down ? "Down" : "Up"}
          </span>
        ) : null}
      </div>
      <div className="card-body">
        {loading ? (
          <Loading label="Loading downtime…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : data ? (
          <>
            <DowntimeStatsBlock stats={data.stats} />

            <div style={{ margin: "12px 0" }}>
              {data.stats.is_down ? (
                <button className="btn small" disabled={stopMut.pending} onClick={onStop}>
                  {stopMut.pending ? <Spinner label="Stopping…" /> : "Mark back up"}
                </button>
              ) : (
                <button className="btn small" onClick={() => setShowStart(true)}>
                  Start downtime
                </button>
              )}
              {stopMut.error ? <Alert kind="error">{stopMut.error}</Alert> : null}
            </div>

            {data.intervals.length === 0 ? (
              <p className="muted">No downtime recorded.</p>
            ) : (
              <ul className="link-list">
                {data.intervals.map((iv) => (
                  <li key={iv.id}>
                    <DowntimeIntervalRow interval={iv} />
                  </li>
                ))}
              </ul>
            )}
          </>
        ) : null}
      </div>

      {showStart ? (
        <StartDowntimeModal
          pending={startMut.pending}
          error={startMut.error}
          onClose={() => setShowStart(false)}
          onSubmit={onStart}
        />
      ) : null}
    </div>
  );
}

function DowntimeStatsBlock({ stats }: { stats: FacilityAssetDowntimeStats }) {
  return (
    <dl className="kv">
      <dt>Availability (90d)</dt>
      <dd>{stats.availability_pct}%</dd>
      <dt>Total downtime (90d)</dt>
      <dd>{formatDowntimeSpan(stats.total_downtime_secs)}</dd>
      {stats.open ? (
        <>
          <dt>Down since</dt>
          <dd>{formatDate(stats.open.started_at)}</dd>
        </>
      ) : null}
    </dl>
  );
}

function DowntimeIntervalRow({ interval }: { interval: FacilityAssetDowntimeRow }) {
  const end = interval.ended_at;
  const span = (end ?? Math.floor(Date.now() / 1000)) - interval.started_at;
  return (
    <div className="facility-linked-wo">
      <div>
        {formatDate(interval.started_at)}
        {" → "}
        {end ? formatDate(end) : <span className="badge danger">ongoing</span>}
      </div>
      <div className="facility-linked-wo-meta">
        <span className="faint">{formatDowntimeSpan(span)}</span>
        {interval.reason ? <span> · {interval.reason}</span> : null}
      </div>
    </div>
  );
}

function StartDowntimeModal({
  pending,
  error,
  onClose,
  onSubmit,
}: {
  pending: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (reason: string) => void;
}) {
  const [reason, setReason] = useState("");
  return (
    <Modal
      title="Start downtime"
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" disabled={pending} onClick={() => onSubmit(reason)}>
            {pending ? <Spinner label="Starting…" /> : "Start"}
          </button>
        </>
      }
    >
      <div className="field">
        <label htmlFor="downtime-reason">Reason (optional)</label>
        <input
          id="downtime-reason"
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. compressor failure"
        />
      </div>
      {error ? <Alert kind="error">{error}</Alert> : null}
    </Modal>
  );
}

/** Service vendors (0085): the vendors that maintain this equipment, with one
 *  optionally flagged primary. "Manage" opens a checkbox picker over the vendor
 *  registry; saving does a full-replace PUT. */
function ServiceVendorsSection({ assetId }: { assetId: string }) {
  const { data, loading, error, reload } = useApi(
    (signal) => listAssetVendors(assetId, signal),
    [assetId],
  );
  const [managing, setManaging] = useState(false);
  const linked = data?.vendors ?? [];

  return (
    <div className="card">
      <div className="card-head">
        <h3>Service vendors</h3>
        <button className="btn ghost small" onClick={() => setManaging(true)}>Manage</button>
      </div>
      <div className="card-body">
        {loading ? (
          <Loading label="Loading vendors…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : linked.length === 0 ? (
          <p className="muted">No vendors assigned. Use Manage to link the companies that service this equipment.</p>
        ) : (
          <ul className="link-list">
            {linked.map((v) => (
              <li key={v.vendor_id} className="facility-linked-wo">
                <div>
                  {v.vendor_name}
                  {v.is_primary ? <span className="badge ok" style={{ marginLeft: 6 }}>Primary</span> : null}
                </div>
                {v.note ? <div className="facility-linked-wo-meta"><span className="faint">{v.note}</span></div> : null}
              </li>
            ))}
          </ul>
        )}
      </div>
      {managing ? (
        <ManageVendorsModal
          assetId={assetId}
          current={linked}
          onClose={() => setManaging(false)}
          onSaved={() => {
            setManaging(false);
            reload();
          }}
        />
      ) : null}
    </div>
  );
}

/** Pick which registry vendors service this asset + flag one primary. */
function ManageVendorsModal({
  assetId,
  current,
  onClose,
  onSaved,
}: {
  assetId: string;
  current: FacilityAssetVendorRow[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { data, loading, error } = useApi((signal) => listFacilityVendors(signal), []);
  const allVendors = data?.vendors ?? [];
  const saveMut = useMutation(setAssetVendors);

  // Working selection: vendor_id → { selected, is_primary }.
  const [selected, setSelected] = useState<Set<string>>(new Set(current.map((v) => v.vendor_id)));
  const [primary, setPrimary] = useState<string | null>(
    current.find((v) => v.is_primary)?.vendor_id ?? null,
  );

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        if (primary === id) setPrimary(null);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  async function onSave() {
    const noteById = new Map(current.map((v) => [v.vendor_id, v.note]));
    const vendors = [...selected].map((vendor_id) => ({
      vendor_id,
      is_primary: primary === vendor_id,
      note: noteById.get(vendor_id) ?? null,
    }));
    const res = await saveMut.run(assetId, vendors);
    if (res) onSaved();
  }

  return (
    <Modal
      title="Service vendors"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={saveMut.pending}>Cancel</button>
          <button className="btn primary" disabled={saveMut.pending} onClick={onSave}>
            {saveMut.pending ? <Spinner label="Saving…" /> : "Save"}
          </button>
        </>
      }
    >
      {saveMut.error ? <Alert kind="error">{saveMut.error}</Alert> : null}
      {loading ? (
        <Loading label="Loading vendors…" />
      ) : error ? (
        <ErrorState error={error} />
      ) : allVendors.length === 0 ? (
        <EmptyState>No vendors yet. Add some under Settings → Facilities → Vendors.</EmptyState>
      ) : (
        <table className="data">
          <thead><tr><th aria-label="Assigned" /><th>Vendor</th><th>Primary</th></tr></thead>
          <tbody>
            {allVendors.map((v) => {
              const isSel = selected.has(v.id);
              return (
                <tr key={v.id}>
                  <td>
                    <input type="checkbox" checked={isSel} onChange={() => toggle(v.id)} aria-label={`Assign ${v.name}`} />
                  </td>
                  <td>{v.name}</td>
                  <td>
                    <input
                      type="radio"
                      name="primary-vendor"
                      checked={primary === v.id}
                      disabled={!isSel}
                      onChange={() => setPrimary(v.id)}
                      aria-label={`Make ${v.name} primary`}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </Modal>
  );
}
